import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import TirSimulator from "../components/TirSimulator";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Truck, Navigation, Eye, MapPin, Boxes } from "lucide-react";
import { Link } from "react-router-dom";

const tl = (n) => (Number(n) || 0).toLocaleString("tr-TR");

export default function TirRota() {
  const [tirs, setTirs] = useState([]);
  const [detail, setDetail] = useState(null); // { tir, pickups }

  const load = () => api.get("/tirs").then((r) => setTirs(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (t) => {
    const r = await api.get(`/tirs/${t.id}/pickups`);
    setDetail({ tir: t, pickups: r.data });
  };

  const groups = useMemo(() => {
    const g = {};
    tirs.forEach((t) => { const k = t.yon || "YÖN BELİRTİLMEMİŞ"; (g[k] = g[k] || []).push(t); });
    return g;
  }, [tirs]);

  const totalCiro = tirs.reduce((a, t) => a + (t.ciro || 0), 0);
  const openCount = tirs.filter((t) => !t.closed).length;

  const ilBreakdown = useMemo(() => {
    if (!detail) return [];
    const m = {};
    detail.pickups.forEach((p) => {
      const d = p.data || {};
      const il = d.alici_il || d.gonderen_il || "—";
      m[il] = m[il] || { il, adet: 0, ciro: 0, kayit: 0 };
      m[il].adet += Number(d.adet) || 0;
      m[il].ciro += Number(d.tutar) || 0;
      m[il].kayit += 1;
    });
    return Object.values(m).sort((a, b) => b.adet - a.adet);
  }, [detail]);

  return (
    <div className="mw-fade-up space-y-5" data-testid="tir-rota-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-white flex items-center gap-2"><Navigation className="h-7 w-7 text-sky-400" /> TIR ROTA / HAT TAKİP</h1>
          <p className="text-sm text-slate-400">TÜM TIR'LAR YÖNLERİNE GÖRE · DOLULUK · CİRO · İÇERİK</p>
        </div>
        <div className="flex gap-2 text-center">
          <div className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2"><div className="text-lg font-bold text-sky-300">{tirs.length}</div><div className="text-[10px] text-slate-500">TIR</div></div>
          <div className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2"><div className="text-lg font-bold text-emerald-300">{openCount}</div><div className="text-[10px] text-slate-500">AÇIK</div></div>
          <div className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-2"><div className="text-lg font-bold text-orange-300">{tl(totalCiro)}</div><div className="text-[10px] text-slate-500">TOPLAM CİRO</div></div>
        </div>
      </div>

      {tirs.length === 0 && <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-16 text-center text-slate-500">HENÜZ TIR AÇILMAMIŞ. KARGO ALIMI EKRANINDAN TIR / HAT AÇABİLİRSİNİZ.</div>}

      {Object.entries(groups).map(([yon, list]) => (
        <div key={yon} className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-sky-300">
            <MapPin className="h-4 w-4" /> {yon} <span className="text-slate-600">· {list.length} TIR</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {list.map((t) => (
              <div key={t.id} data-testid={`rota-tir-${t.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-sky-400" />
                    <span className="font-head text-lg font-bold text-white">{t.plate || t.id}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${t.closed ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>{t.closed ? "KAPALI" : "AÇIK"}</span>
                  </div>
                  <Link data-testid={`rota-3d-${t.id}`} to={`/tir-plan/${t.id}`} className="ml-auto mr-1 rounded-md border border-amber-600/50 bg-amber-950/40 px-2 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-900/40">3D YÜKLEME</Link>
                  <Button data-testid={`rota-detail-${t.id}`} size="sm" variant="outline" onClick={() => openDetail(t)} className="h-7 border-slate-700 bg-slate-800 text-slate-200"><Eye className="mr-1 h-3.5 w-3.5" /> DETAY</Button>
                </div>
                <div className="mb-2 text-xs text-slate-400">{t.driver || "SÜRÜCÜ YOK"}{t.hatlar?.length ? " · " + t.hatlar.join(" → ") : ""}</div>
                <TirSimulator yuk={t.yuk} cap_palet={t.cap_palet} cap_koli={t.cap_koli} />
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-400">{t.kayit || 0} ALIM</span>
                  <span className="text-slate-400">CİRO: <b className="text-orange-300">{tl(t.ciro)}</b> · NAVLUN: {tl(t.navlun)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Truck className="h-5 w-5 text-sky-400" /> {detail?.tir.plate || detail?.tir.id} — İÇERİK ({detail?.pickups.length || 0} ALIM)</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <TirSimulator yuk={detail.tir.yuk} cap_palet={detail.tir.cap_palet} cap_koli={detail.tir.cap_koli} height={120} />
              <div>
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-400"><Boxes className="h-3.5 w-3.5" /> VARIŞ İLİ BAZLI</div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {ilBreakdown.map((x) => (
                    <div key={x.il} className="rounded-lg bg-slate-800 p-2 text-xs"><div className="font-bold text-slate-100">{x.il}</div><div className="text-slate-400">{x.adet} adet · {x.kayit} alım · {tl(x.ciro)}</div></div>
                  ))}
                </div>
              </div>
              <div className="overflow-auto rounded-lg border border-slate-800">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800/60 text-left uppercase tracking-wider text-slate-500">
                    <tr><th className="px-2 py-2">TARİH</th><th className="px-2 py-2">GÖNDERİCİ</th><th className="px-2 py-2">ALICI</th><th className="px-2 py-2">VARIŞ</th><th className="px-2 py-2">TÜR</th><th className="px-2 py-2 text-right">TUTAR</th></tr>
                  </thead>
                  <tbody>
                    {detail.pickups.length === 0 && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-500">BU TIR'A BAĞLI ALIM YOK</td></tr>}
                    {detail.pickups.map((p) => { const d = p.data || {}; return (
                      <tr key={p.id} data-testid={`rota-pickup-${p.id}`} className="border-b border-slate-800/60 text-slate-200">
                        <td className="px-2 py-1.5 font-mono text-slate-400">{d.islem_tarihi}</td>
                        <td className="px-2 py-1.5">{d.gonderici_firma}</td>
                        <td className="px-2 py-1.5">{d.alici_firma || d.alici_musteri}</td>
                        <td className="px-2 py-1.5 text-slate-400">{d.alici_ilce}/{d.alici_il}</td>
                        <td className="px-2 py-1.5">{d.adet}× {d.kargo_turu}</td>
                        <td className="px-2 py-1.5 text-right">{d.tutar ? tl(d.tutar) : "—"}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
